/**
 * useInzaraEvents — listen to postMessage events sent by any embedded
 * INZARA page. Handles origin filtering, cleanup, and stale-closure
 * safety via a ref for the callback.
 *
 *   useInzaraEvents('https://analytics.example.com', (event) => {
 *     console.log(event.type, event);
 *   });
 *
 * `origin` MUST match the exact origin the embed is served from (scheme
 * + host + port). Passing '*' is accepted but strongly discouraged.
 */
import { useEffect, useRef } from 'react';

export function useInzaraEvents(origin, onEvent) {
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    function listener(ev) {
      // Never trust data from a foreign origin.
      if (origin && origin !== '*' && ev.origin !== origin) return;
      const payload = ev?.data;
      // The embed pages send `{ source: 'inzara.embed', type, payload }`.
      // Ignore anything else (React DevTools, iframe libs, etc.).
      if (!payload || typeof payload !== 'object') return;
      if (payload.source !== 'inzara.embed') return;
      if (typeof payload.type !== 'string') return;
      try { handlerRef.current && handlerRef.current(payload); }
      catch (err) {
         
        console.error('[@inzara/embed-react] onEvent handler threw:', err);
      }
    }
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [origin]);
}
